import React, { useCallback, useMemo } from 'react';

import {
  DashIcon, PlusIcon, XCircleFillIcon, XIcon 
} from '@primer/octicons-react';
import { Button as PrimerButton, StyledOcticon } from '@primer/react';

import classNames from 'classnames';
import isFunction from 'lodash/isFunction';

import Label from '../Labels/Label';

type SimpleButtonArgTypes = {
  className?: string,
  children?: React.ReactNode,
  onClick?: React.MouseEventHandler<HTMLButtonElement> | undefined,
  disabled?: boolean,
}

type ButtonArgTypes = {
  className?: string,
  children?: React.ReactNode,
  onClick?: React.MouseEventHandler<HTMLButtonElement> | undefined,
  textColor?: string,
  hoverTextColor?: string,
  activeTextColor?: string,
  border?: boolean,
  sx?: any,
  size?: string,
  variant?: string,
  iconSize?: number,
  disabled?: boolean
}

export const BUTTON_VARIANT = {
  DEFAULT: 'default',
  BRAND: 'brand',
  ALTERNATE: 'brand2',
  PRIMARY: 'primary',
  INVISIBLE: 'invisible',
  DANGER: 'danger',
  CLOSE: 'close',
  CIRCLE_CLOSE: 'circle_close',
  ADD: 'add',
  REMOVE: 'remove',
}

const AddButton: React.FC<ButtonArgTypes> = ({ 
  className = '',
  onClick = () => {},
  textColor = 'border-dark',
  hoverTextColor = 'border-darker',
  activeTextColor = 'info',
  border = true,
  ...rest 
}) => (
  <button
    className={
      classNames(
        'flex justify-center items-center p-1 h-8 w-8',
        `border-${textColor} text-${textColor} hover:text-${hoverTextColor} hover:border-${hoverTextColor} active:${activeTextColor} active:border-${activeTextColor}`,
        { 'rounded border': border },
        className
      )
    }
    type="button"
    aria-label="add"
    onClick={onClick}
    {...rest}
  >
    <StyledOcticon icon={PlusIcon} size="small" />
  </button>
)

const BrandButton: React.FC<SimpleButtonArgTypes> = ({
  children = null, 
  className = undefined,
  disabled = false,
  onClick = () => {},
}) => (
  <button
    className={
      classNames(
        'flex justify-center items-center border text-background rounded-md px-4 py-1',
        { 'bg-highlighted': disabled },
        { 'bg-brand border-border-brand hover:bg-border-brand active:bg-brandlight': !disabled },
        className
      )
    }
    type="button"
    onClick={onClick}
    disabled={disabled}
  >
    <Label size="sm" color="background" bold>{children}</Label>
  </button>
)

const RemoveButton: React.FC<ButtonArgTypes> = ({
  className = '',
  onClick = () => {},
  textColor = 'border-dark',
  hoverTextColor = 'border-darker',
  activeTextColor = 'info',
  border = true,
  ...rest
}) => (
  <button
    className={
      classNames(
        'flex justify-center items-center p-1 h-8 w-8',
        `border-${textColor} text-${textColor} hover:text-${hoverTextColor} hover:border-${hoverTextColor} active:${activeTextColor} active:border-${activeTextColor}`,
        { 'rounded border': border },
        className
      )
    }
    type="button"
    aria-label="remove"
    onClick={onClick}
    {...rest}
  >
    <StyledOcticon icon={DashIcon} size="small" />
  </button>
)

const CircleCloseButton: React.FC<ButtonArgTypes> = ({
  className = '',
  onClick = () => {},
  textColor = 'border-light',
  hoverTextColor = 'border-dark',
  activeTextColor = 'border-darker',
  children = null,
  ...rest
}) => (
  <button
    className={
      classNames(
        `text-${textColor} hover:text-${hoverTextColor} active:text-${activeTextColor}`,
        className,
      )
    }
    type="button"
    aria-label="Close"
    onClick={onClick}
    {...rest}
  >
    <StyledOcticon icon={XCircleFillIcon} size="small" />
    { children }
  </button>
);

const CloseButton: React.FC<ButtonArgTypes> = ({
  className = '',
  onClick = () => {},
  textColor = 'border-light',
  hoverTextColor = 'border-dark',
  activeTextColor = 'border-darker',
  ...rest
}) => (
  <button
    className={
      classNames(
        `h-8 w-8 text-${textColor} hover:text-${hoverTextColor} active:text-${activeTextColor}`,
        className,
      )
    }
    type="button"
    aria-label="Close"
    onClick={onClick}
    {...rest}
  >
    <StyledOcticon icon={XIcon} size="small" />
  </button>
);

const getButtonComponent = (variant = BUTTON_VARIANT.DEFAULT) => {
  switch (variant) {
  case BUTTON_VARIANT.CLOSE:
    return CloseButton;
  case BUTTON_VARIANT.CIRCLE_CLOSE:
    return CircleCloseButton;
  case BUTTON_VARIANT.BRAND:
    return BrandButton;
  case BUTTON_VARIANT.ADD:
    return AddButton;
  case BUTTON_VARIANT.REMOVE:
    return RemoveButton;
  default:
    return PrimerButton;
  }
}

export const Button: React.FC<ButtonArgTypes> = ({
  children = [],
  onClick = () => {},
  size = 'medium',
  variant = BUTTON_VARIANT.DEFAULT,
  ...rest
}) => {

  const handleClick = useCallback((e: React.MouseEvent) => {
    if (isFunction(onClick)) {
      onClick(e);
    }
  }, [onClick])

  const ButtonComponent = useMemo(() => getButtonComponent(variant), [variant]);

  return (
    <ButtonComponent
      variant={variant}
      size={size}
      onClick={(e: React.MouseEvent) => handleClick(e)}
      {...rest}
    >
      { children }
    </ButtonComponent>
  )
}

export default Button;
